'use client';

/**
 * HomepageV3 — editorial ("Bordeaux editorial") homepage.
 *
 * Composes the editorial sections in the exact order of the source page
 * builder (`design-refs/source/private-home.js` → `buildVersionA`, logged-out
 * `SE_VIEW="out"`, `SE_FEATURED="disruptors"`):
 *
 *   header(banner + nav + hero + ticker + disruptors spotlight)
 *   → offering shelves (Trending / Closing soon / Low minimum)
 *   → portfolio-strategy quote band (.ps)
 *   → why-private manifesto (.wp)
 *   → how-it-works → app-download bottom → footer
 *
 * Tokens:   ./theme/tokens         (typed JS design tokens)
 * CSS vars: ./theme/globalStyles   (EditorialRoot wrapper + editorialRootVars)
 * Content:  ./data/demoData        (exact demo companies / offerings)
 */
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Route } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import * as Sentry from 'utils/errorTracking';
import { getSe2Experience, trackEvent, trackEventWithGTM } from 'utils/analytics';
import useLoginModal from 'hooks/useLoginModal';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useGetUser from 'hooks/useGetUser';
import useGetAccreditationEligibility from 'hooks/useGetAccreditationEligibility';
import useHomepageV2Config from 'hooks/useHomepageV2Config';
import useStrapiBanner from 'hooks/useStrapiBannerData';
import EditorialRoot, { editorialScrollGlobalStyles } from './theme/globalStyles';
import { COMPANIES, type Company, type OfferingCardData } from './data/demoData';
import useHomepageV3Offerings from './data/useHomepageV3Offerings';
import { allocatePreviewSections, buildCatalogPreviewSections } from './utils/previewSections';
import useGetElasticOfferingsInfinite from 'hooks/search/useGetElasticOfferingsInfinite';
import { getOffering, getOfferingsBatch } from 'services/offerings';
import {
  buildSe3ShelvesFromElastic,
  hydrateRegDAvailability,
  hydrateRegDDisclosureEntities,
  mergeSe3FilteredSections,
  type ElasticOffering,
} from './utils/elasticExplore';
import { buildSe3ExploreRoute, useSe3QaSearch, withSe3QaOverride } from './utils/routes';
import EditorialNav from './components/EditorialNav';
import EditorialActionBanner from './components/EditorialActionBanner';
import AnnouncementBanner from './components/AnnouncementBanner';
import { deriveEditorialNavAuth } from './utils/navAuth';
import HeroSection from './sections/HeroSection';
import PastOfferingsTicker from './sections/PastOfferingsTicker';
import OfferingSpotlight from './sections/OfferingSpotlight';
import FilterSidebar, { type FilterSelection } from './components/FilterSidebar';
import FilterResults from './components/FilterResults';
import EditorialQuoteSection from './sections/EditorialQuoteSection';
import WpManifestoSection from './sections/WpManifestoSection';
import HowToStartSection from './sections/HowToStartSection';
import AppDownloadSection from './sections/AppDownloadSection';
import EditorialFooter from './sections/EditorialFooter';
import {
  HomepageTombstoneVariantProvider,
  useHomepageTombstoneVariant,
} from './experiments/tombstoneExperiment';

export interface HomepageV3Props {
  /** The standalone /v3 design-preview route keeps static handoff data. */
  liveOfferings?: boolean;
}

const captureTrackingError = (e: unknown) =>
  Sentry.captureException(e, { tags: { area: 'homepage_v3' } });

const HOMEPAGE_HANDOFF_HERO_VARIANT = 'se3_fallback';
const HOMEPAGE_BROWSE_ID = 'homepage-v3-browse';

interface HomeSpotlightFromSlot {
  href: string;
  imageSrc?: string;
  spotlight: {
    eyebrowLabel: string;
    series: string;
    name: string;
    desc: string;
    cta: string;
    disclosureLegal?: string;
  };
}

const stripHtml = (value?: string) =>
  value
    ?.replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim() ?? '';

const controlHomepageHref = (href?: string) => {
  if (!href?.trim()) return undefined;
  try {
    const url = new URL(href, 'https://www.startengine.com');
    return url.origin === 'https://www.startengine.com' ? `${url.pathname}${url.search}` : href;
  } catch {
    return href;
  }
};

const offeringSlugFromHref = (href?: string): string | undefined => {
  if (!href?.trim()) return undefined;
  try {
    const url = new URL(href, 'https://www.startengine.com');
    if (url.origin !== 'https://www.startengine.com') return undefined;
    const [, offering, rawSlug] = url.pathname.split('/');
    if (offering !== 'offering' || !rawSlug) return undefined;
    return decodeURIComponent(rawSlug);
  } catch {
    return undefined;
  }
};

type CampaignSpotlightOffering = {
  name?: string;
  companyName?: string;
  company?: { dbaName?: string; name?: string };
  coverTombstoneUrl?: string;
};

const campaignDisplayName = (offering?: CampaignSpotlightOffering): string | undefined =>
  [offering?.company?.dbaName, offering?.company?.name, offering?.name, offering?.companyName]
    .find((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    ?.trim();

const strapiImageUrl = (image: unknown): string | undefined => {
  if (typeof image === 'string') return image;
  if (!image || typeof image !== 'object') return undefined;
  const record = image as { url?: unknown; data?: { attributes?: { url?: unknown } } };
  const url = record.data?.attributes?.url ?? record.url;
  return typeof url === 'string' && url.trim() ? url : undefined;
};

const buildHomeSpotlightFromControlBanner = (
  attributes: Record<string, unknown> | undefined,
  campaignOffering?: CampaignSpotlightOffering,
): HomeSpotlightFromSlot | null => {
  const title =
    campaignDisplayName(campaignOffering) ??
    stripHtml(attributes?.hero_title_desktop as string | undefined);
  const href = controlHomepageHref(attributes?.hero_button_link_desktop as string | undefined);
  if (!title || !href) return null;
  const imageSrc =
    (typeof campaignOffering?.coverTombstoneUrl === 'string' &&
      campaignOffering.coverTombstoneUrl.trim()) ||
    strapiImageUrl(attributes?.hero_image);
  const legal = stripHtml(attributes?.hero_discloser as string | undefined);
  return {
    href,
    ...(imageSrc ? { imageSrc } : {}),
    spotlight: {
      eyebrowLabel: 'Featured Offering',
      series: '',
      name: title,
      desc:
        stripHtml(attributes?.hero_subheading_desktop as string | undefined) ||
        `Invest in ${title} on StartEngine.`,
      cta:
        stripHtml(
          (attributes?.hero_button_copy_desktop ?? attributes?.hero_button_text_desktop) as
            | string
            | undefined,
        ) || 'View opportunity',
      ...(legal ? { disclosureLegal: legal } : {}),
    },
  };
};

/**
 * PD-35991 / PD-36143 — `.se-layout`: sticky 256px filter sidebar + offering
 * content, mirroring the source `wrapWithSidebar(...)` desktop browse layout.
 * At ≤1080px the desktop rail collapses into the shared compact filter drawer
 * trigger so mobile/tablet users retain equivalent browse/filter controls.
 */
const Layout = styled('div')({
  display: 'grid',
  gridTemplateColumns: '256px 1fr',
  alignItems: 'start',
  background: 'var(--cream)',
  // Sticky nav is 79px (desktop/tablet) / 67px (mobile); clear it on smooth-scroll
  // from the hero "Explore investments" CTA so the "Trending now" title isn't
  // parked under the nav (Paige QA).
  scrollMarginTop: '96px',
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
  },
});

/** `.se-content` — bordered offering column beside the sidebar. */
const Content = styled('div')({
  padding: '44px 0 56px',
  background: 'var(--cream)',
  minWidth: 0,
  borderLeft: '1px solid var(--edge)',
  '@media (max-width: 1080px)': {
    borderLeft: 'none',
  },
  '@media (max-width: 640px)': {
    paddingTop: '24px',
  },
});

export default function HomepageV3({ liveOfferings = true }: HomepageV3Props) {
  const tombstoneVariant = useHomepageTombstoneVariant();
  const hasTrackedView = useRef(false);
  const browseRef = useRef<HTMLDivElement | null>(null);
  const [viewTrackingAttempt, setViewTrackingAttempt] = useState(0);

  // PD-35994 — wire the editorial auth CTAs (nav Log In / Sign Up, hero CTA,
  // app-download signup) to the single shared login modal. Previously these
  // handlers only fired tracking and the buttons did nothing.
  const [renderLoginModal, setLoginModalOpen] = useLoginModal() as [
    (config?: Record<string, unknown>) => ReactNode,
    (open: boolean) => void,
    boolean,
  ];

  const isSignedIn = useIsSignedIn();
  const { data: userData } = useGetUser();
  const { isAccredited, isQualifiedPurchaser } = useGetAccreditationEligibility({
    primaryUserId: userData?.primaryUserId,
  });
  const investmentAccountStatus = useSelector(
    (state: { user?: { investmentAccountStatus?: Record<string, unknown> } }) =>
      state.user?.investmentAccountStatus,
  );
  const hasInvestmentAccount = Boolean(
    investmentAccountStatus?.hasInvestmentAccount ||
    investmentAccountStatus?.investmentAccountPending,
  );
  const { variant: navVariant, accountName: navAccountName } = deriveEditorialNavAuth(isSignedIn);

  const experience = useMemo(
    () =>
      getSe2Experience({
        isLoggedIn: isSignedIn === true,
        hasInvestmentAccount,
        isAccreditedAccount: isAccredited === true,
      }),
    [isSignedIn, hasInvestmentAccount, isAccredited],
  );
  const { bannerText } = useHomepageV2Config({ skipOfferingsBatch: true });
  const announcementText = useMemo(() => {
    if (isQualifiedPurchaser && bannerText?.qp) return bannerText.qp;
    if (experience === 'retail') return bannerText?.default;
    return bannerText?.accredited || bannerText?.default;
  }, [bannerText, experience, isQualifiedPurchaser]);
  const authState = useMemo(() => {
    if (isSignedIn === true) return 'signed_in';
    if (isSignedIn === false) return 'signed_out';
    return 'unknown';
  }, [isSignedIn]);

  useEffect(() => {
    // `useIsSignedIn` is tri-state; wait for it to resolve before tracking so
    // the impression carries a real auth_state (not 'unknown') and the nav
    // variant it reports matches the header the visitor actually saw. Mirrors
    // the guard in CanadaLandingPage.
    if (hasTrackedView.current || isSignedIn === null) return undefined;
    // Claim the view synchronously: this effect re-runs as auth/experience
    // resolve, and setting the flag before the async call prevents a duplicate
    // impression from firing in the gap before it settles.
    hasTrackedView.current = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    trackEvent('viewed_homepage_v3', {
      page: 'homepage_v3',
      variant: navVariant,
      hero_variant: HOMEPAGE_HANDOFF_HERO_VARIANT,
      se2_experience: experience,
      auth_state: authState,
    }).catch((e) => {
      captureTrackingError(e);
      if (viewTrackingAttempt < 2) {
        // Release the claim and schedule a retry with the same payload.
        hasTrackedView.current = false;
        retryTimer = setTimeout(
          () => setViewTrackingAttempt((attempt) => attempt + 1),
          1000 * (viewTrackingAttempt + 1),
        );
      }
    });

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isSignedIn, navVariant, experience, authState, viewTrackingAttempt]);

  const router = useRouter();
  const qaSearch = useSe3QaSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSelection, setFilterSelection] = useState<FilterSelection | null>(null);
  const [resultsFocusKey, setResultsFocusKey] = useState(0);
  const liveOfferingData = useHomepageV3Offerings({
    enabled: liveOfferings,
    // Retail (logged-in, non-accredited) users see only Reg CF / Reg A — no Reg D.
    excludeRegD: experience === 'retail',
  });
  const hasLiveOfferings = liveOfferings && liveOfferingData.hasLiveData;

  const browseSections = hasLiveOfferings ? liveOfferingData.sections : undefined;
  const browseFullSections = hasLiveOfferings ? liveOfferingData.fullSections : undefined;
  const browseLoading = liveOfferings && liveOfferingData.isLoading;

  // The tombstones feed is a small, personalized preview. The full Elastic
  // catalog is the source for both sidebar counts AND filtered/browse results;
  // otherwise a “15” count can open a grid containing only its five preview
  // cards. Tombstone cards still replace matching Elastic cards below so their
  // richer live availability/progress data is retained where available.
  const elasticCountQuery = useGetElasticOfferingsInfinite({
    query: ' ',
    size: 250,
    categoryLabel: undefined,
    sortBy: 'mostMomentum',
    enabled: liveOfferings,
    regulationTypes: undefined,
  });
  const elasticCatalog = useMemo(() => {
    const list = (
      (elasticCountQuery.data?.pages ?? []) as Array<{ list?: ElasticOffering[] }>
    ).flatMap((page) => page?.list ?? []);
    const built = buildSe3ShelvesFromElastic(list, {
      excludeRegD: experience === 'retail',
      amountVisibility: liveOfferingData.amountVisibility,
    });
    return built.fullSections.length > 0 ? built : undefined;
  }, [elasticCountQuery.data, experience, liveOfferingData.amountVisibility]);

  // Elastic drives the complete filter catalog but does not expose Reg D's
  // lockedOnWaitlist/isOverMax state. Hydrate the visible private offerings
  // from the authoritative offering endpoint so a waitlisted raise never
  // falls back to a blank, misleading progress meter.
  const catalogRegDSlugs = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(elasticCatalog?.companies ?? {})
            .filter((company) => company.reg.startsWith('Reg D'))
            .map((company) => company.key),
        ),
      ),
    [elasticCatalog?.companies],
  );
  const catalogAvailabilityQuery = useQuery({
    queryKey: ['homepageV3CatalogAvailability', catalogRegDSlugs],
    queryFn: () => getOfferingsBatch(catalogRegDSlugs),
    enabled: liveOfferings && catalogRegDSlugs.length > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const browseFullSectionsWithCatalog = useMemo(() => {
    if (!elasticCatalog) return browseFullSections;
    // A live card may belong to a different curated shelf than the Elastic
    // ranking where it appears. Match across every live shelf so its
    // availability state (for example a full, waitlisted Reg D offering) is
    // never replaced by Elastic's raised/minimum-only representation.
    const liveCards = new Map(
      (browseFullSections ?? []).flatMap((section) =>
        section.cards.map((card) => [card.co, card] as const),
      ),
    );
    const mergedSections = mergeSe3FilteredSections(
      elasticCatalog.fullSections,
      browseFullSections,
    );
    return (mergedSections ?? []).map((catalogSection) => {
      return {
        ...catalogSection,
        cards: catalogSection.cards.map((card) => {
          const liveCard = liveCards.get(card.co);
          if (liveCard) return liveCard;
          const availabilityCard = hydrateRegDAvailability(
            card,
            catalogAvailabilityQuery.data?.get(card.co) as ElasticOffering | undefined,
          );
          const eligibilityMeta = liveOfferingData.eligibilityBadges[card.co.toLowerCase()];
          return eligibilityMeta
            ? { ...availabilityCard, meta: eligibilityMeta }
            : availabilityCard;
        }),
      };
    });
  }, [
    browseFullSections,
    catalogAvailabilityQuery.data,
    elasticCatalog,
    liveOfferingData.eligibilityBadges,
  ]);
  const browseSectionsWithCatalog = useMemo(
    () =>
      browseFullSectionsWithCatalog && browseFullSections
        ? allocatePreviewSections(
            buildCatalogPreviewSections(browseFullSections, browseFullSectionsWithCatalog),
          )
        : browseSections,
    [browseFullSections, browseFullSectionsWithCatalog, browseSections],
  );

  const activeCompanies = useMemo<Record<string, Company>>(
    () =>
      hydrateRegDDisclosureEntities(
        {
          ...COMPANIES,
          ...elasticCatalog?.companies,
          ...(hasLiveOfferings ? liveOfferingData.companies : {}),
        },
        catalogAvailabilityQuery.data as Map<string, ElasticOffering> | undefined,
      ),
    [
      catalogAvailabilityQuery.data,
      elasticCatalog?.companies,
      hasLiveOfferings,
      liveOfferingData.companies,
    ],
  );
  const liveSpotlightHref = hasLiveOfferings ? liveOfferingData.spotlightHref : undefined;

  const { data: controlHomepageBanner, isLoading: isControlHomepageLoading } = useStrapiBanner({
    queryKey: 'homepageCMSData',
    flag: 'homepages',
  });
  const homepageSpotlightHref = controlHomepageHref(
    controlHomepageBanner?.attributes?.hero_button_link_desktop as string | undefined,
  );
  const homepageSpotlightSlug = useMemo(
    () => offeringSlugFromHref(homepageSpotlightHref),
    [homepageSpotlightHref],
  );
  const {
    data: homepageSpotlightOffering,
    isLoading: isHomepageSpotlightLoading,
    isFetching: isHomepageSpotlightFetching,
  } = useQuery({
    queryKey: ['homepageCampaignSpotlight', homepageSpotlightSlug],
    queryFn: () => getOffering({ slug: homepageSpotlightSlug ?? '' }),
    enabled: Boolean(homepageSpotlightSlug),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const strapiSpotlight = useMemo(() => {
    if (homepageSpotlightSlug && !homepageSpotlightOffering) return null;
    return buildHomeSpotlightFromControlBanner(
      controlHomepageBanner?.attributes as Record<string, unknown> | undefined,
      homepageSpotlightOffering as CampaignSpotlightOffering | undefined,
    );
  }, [controlHomepageBanner, homepageSpotlightOffering, homepageSpotlightSlug]);
  // Do not render a live default while Strapi is resolving. A live tombstone
  // spotlight is retained solely as the published-empty CMS fallback.
  const hasStrapiSpotlight = Boolean(strapiSpotlight);
  const isLoadingSpotlight =
    isControlHomepageLoading ||
    (Boolean(homepageSpotlightSlug) &&
      (isHomepageSpotlightLoading || isHomepageSpotlightFetching)) ||
    (!hasStrapiSpotlight && liveOfferingData.isLoading);
  const hasLiveSpotlight =
    !isLoadingSpotlight && Boolean(liveSpotlightHref && liveOfferingData.spotlight);
  const resolvedSpotlight = strapiSpotlight
    ? strapiSpotlight.spotlight
    : hasLiveSpotlight
      ? (liveOfferingData.spotlight ?? undefined)
      : undefined;
  const resolvedSpotlightImage = strapiSpotlight
    ? strapiSpotlight.imageSrc
    : hasLiveSpotlight
      ? liveOfferingData.spotlightImage
      : undefined;
  const spotlightHref = strapiSpotlight
    ? strapiSpotlight.href
    : hasLiveSpotlight
      ? liveSpotlightHref
      : undefined;
  const spotlightLegal = strapiSpotlight
    ? strapiSpotlight.spotlight.disclosureLegal
    : hasLiveSpotlight
      ? liveOfferingData.spotlight?.disclosureLegal
      : undefined;

  const handleSearchClick = () => {
    trackEvent('search_icon_click', { page: 'homepage_v3', surface: 'editorial_nav' }).catch(
      captureTrackingError,
    );
  };

  // Search lives on the canonical /explore surface with the SE3 override. Stage
  // gateway redirects /v3/explore through /offering, so avoid that route here.
  const handleSearchSubmit = (value: string) => {
    const query = (value ?? '').trim();
    trackEvent('homepage_v3_search_submit', {
      page: 'homepage_v3',
      surface: 'editorial_nav',
    }).catch(captureTrackingError);
    if (query) {
      router.push(buildSe3ExploreRoute(query) as Route);
    }
  };

  const handleLogInClick = () => {
    try {
      trackEventWithGTM('clicked_homepage_v3_log_in', {
        page: 'homepage_v3',
        surface: 'editorial_nav',
      });
    } catch (e) {
      captureTrackingError(e);
    }
    setLoginModalOpen(true);
  };

  const handleSignUpClick = () => {
    try {
      trackEventWithGTM('clicked_homepage_v3_sign_up', {
        page: 'homepage_v3',
        surface: 'editorial_nav',
      });
    } catch (e) {
      captureTrackingError(e);
    }
    setLoginModalOpen(true);
  };

  const handleHeroCtaClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    try {
      trackEventWithGTM('homepage_v3_hero_cta_clicked', { page: 'homepage_v3', surface: 'hero' });
    } catch (e) {
      captureTrackingError(e);
    }
    const browseTarget = browseRef.current ?? document.getElementById(HOMEPAGE_BROWSE_ID);
    browseTarget?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAppSignUpClick = () => {
    setLoginModalOpen(true);
  };

  const handleAccessCtaClick = (surface: string) => {
    try {
      trackEventWithGTM('clicked_homepage_v3_access_cta', {
        page: 'homepage_v3',
        surface,
      });
    } catch (e) {
      captureTrackingError(e);
    }
    setLoginModalOpen(true);
  };

  const handleStartInvestingClick = () => {
    try {
      trackEventWithGTM('clicked_homepage_v3_access_cta', {
        page: 'homepage_v3',
        surface: 'why_private',
      });
    } catch (e) {
      captureTrackingError(e);
    }
    const browseTarget = browseRef.current ?? document.getElementById(HOMEPAGE_BROWSE_ID);
    browseTarget?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSpotlightCtaClick = () => {
    if (spotlightHref) {
      router.push(withSe3QaOverride(spotlightHref, qaSearch) as Route);
      return;
    }
    setLoginModalOpen(true);
  };

  const handleViewCard = (card: OfferingCardData) => {
    const company = activeCompanies[card.co];
    const href =
      (hasLiveOfferings ? liveOfferingData.hrefByCo[card.co] : undefined) ?? company?.href ?? null;
    trackEvent('homepage_v3_offering_card_view_details', {
      page: 'homepage_v3',
      company: company?.name ?? card.co,
      company_key: card.co,
      series: company?.series,
      section: company?.sector,
      is_live: Boolean(hasLiveOfferings && href),
      destination: href ?? 'auth_modal',
    }).catch(captureTrackingError);
    if (href) {
      router.push(withSe3QaOverride(href, qaSearch) as Route);
    } else {
      setLoginModalOpen(true);
    }
  };

  const handleSidebarSelect = (selection: FilterSelection) => {
    trackEvent('homepage_v3_filter_sidebar_select', {
      page: 'homepage_v3',
      surface: 'filter_sidebar',
      group: selection.group,
      item: selection.item.key,
      item_type: selection.item.type,
    }).catch(captureTrackingError);
    setFilterSelection(selection);
    setResultsFocusKey((key) => key + 1);
  };

  const handleFilterClear = () => {
    const hadSelection = filterSelection != null;
    setFilterSelection(null);
    if (hadSelection) setResultsFocusKey((key) => key + 1);
  };

  return (
    <>
      <GlobalStyles styles={editorialScrollGlobalStyles} />
      <EditorialRoot data-cy="homepage-v3">
        <EditorialActionBanner />
        {announcementText && <AnnouncementBanner>{announcementText}</AnnouncementBanner>}
        <EditorialNav
          variant={navVariant}
          accountName={navAccountName}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          searchSuggestions={Object.values(activeCompanies)
            .filter((company): company is Company & { href: string } => Boolean(company.href))
            .map((company) => ({
              label: company.name,
              href: company.href,
              searchTerms: [company.key, company.series, company.companyName].filter(
                (term): term is string => Boolean(term),
              ),
            }))}
          onSearchClick={handleSearchClick}
          onLogInClick={handleLogInClick}
          onSignUpClick={handleSignUpClick}
        />
        {/* PD-36000 — the global app/layout.jsx already wraps page content in
          <main id="main-content">. Use a plain div here so the SE3 homepage
          does not introduce a second `main` landmark (QA measured mains=2). */}
        <div data-cy="homepage-v3-main">
          {/* `useIsSignedIn` is intentionally tri-state. Keep the public hero
              out of the DOM while the session resolves so signed-in visitors
              never see it flash. Use the existing campaign-panel skeleton,
              rather than a bespoke loading message, to preserve the editorial
          page treatment during that brief transition. */}
          {isSignedIn === null && (
            <OfferingSpotlight isLoadingSpotlight data-cy="homepage-v3-auth-loading" />
          )}
          {isSignedIn === false && (
            <HeroSection
              eyebrow=""
              heroVariant={HOMEPAGE_HANDOFF_HERO_VARIANT}
              titleFootnote="1"
              ctaHref={`#${HOMEPAGE_BROWSE_ID}`}
              onCtaClick={handleHeroCtaClick}
            />
          )}
          {/* Logged-out: the "as seen" logo strip sits above the featured offering. */}
          {isSignedIn === false && <PastOfferingsTicker />}
          {isSignedIn !== null && (
            <OfferingSpotlight
              variant="default"
              // Hold the band empty while the direct PsiQuantum lookup resolves.
              // Once visible, all spotlight fields come from the same offering.
              isLoadingSpotlight={isLoadingSpotlight}
              spotlight={resolvedSpotlight}
              imageSrc={resolvedSpotlightImage}
              alignImageWithTitle
              imageAlt={resolvedSpotlight?.name}
              disclosure={
                spotlightHref
                  ? {
                      terms: (
                        <Link href={withSe3QaOverride(spotlightHref, qaSearch) as Route}>
                          Full details →
                        </Link>
                      ),
                      legal:
                        spotlightLegal ??
                        'This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment. Review the offering materials before investing.',
                    }
                  : undefined
              }
              onCtaClick={handleSpotlightCtaClick}
            />
          )}
          {/* Logged-in: the logo strip (and its disclaimer) sits BELOW the featured
              offering rather than above it (QA). Logged-out keeps it above. */}
          {isSignedIn === true && <PastOfferingsTicker />}
          <Layout
            id={HOMEPAGE_BROWSE_ID}
            ref={browseRef}
            className="se-layout"
            data-cy="homepage-v3-layout"
          >
            <FilterSidebar
              view="private"
              // PD (Paige QA batch 3): homepage keeps the expanded (non-dropdown)
              // sidebar; Explore owns the dropdown + full industry taxonomy.
              filterMode="home"
              sections={browseFullSectionsWithCatalog}
              activeKey={filterSelection?.item.key ?? null}
              onSelect={handleSidebarSelect}
              onClear={handleFilterClear}
            />
            <Content className="se-content" data-cy="homepage-v3-content">
              <HomepageTombstoneVariantProvider value={tombstoneVariant}>
                <FilterResults
                  selection={filterSelection}
                  sections={browseSectionsWithCatalog}
                  fullSections={browseFullSectionsWithCatalog}
                  companies={activeCompanies}
                  // Show shelf skeletons while the catalog loads instead of the
                  // demo-data relic tombstones (QA).
                  isLoading={browseLoading}
                  focusOnUpdateKey={resultsFocusKey}
                  onClear={handleFilterClear}
                  onViewCard={handleViewCard}
                  onNotify={() => setLoginModalOpen(true)}
                />
              </HomepageTombstoneVariantProvider>
            </Content>
          </Layout>
          <EditorialQuoteSection />
          <WpManifestoSection ctaLabel="Start Investing" onCtaClick={handleStartInvestingClick} />
          <HowToStartSection onCtaClick={() => handleAccessCtaClick('how_to_start')} />
          <AppDownloadSection onSignUpClick={handleAppSignUpClick} />
        </div>
        <EditorialFooter />
        {renderLoginModal({ subtitle: 'Sign up or log in to continue' })}
      </EditorialRoot>
    </>
  );
}

export { default as EditorialRoot, editorialRootVars } from './theme/globalStyles';
export * from './theme/tokens';
export * from './data/demoData';
